import { BaseTool } from './BaseTool';
import { ToolType } from '../types';

export class HealingTool extends BaseTool {
  public id: ToolType = 'heal';
  public name = 'Spot Healing & Blemish Repair';
  public cursor = 'none';

  private isPainting = false;
  private currentMouseX = 0;
  private currentMouseY = 0;

  public onMouseDown(e: MouseEvent, canvasX: number, canvasY: number): void {
    const layer = this.editor.getActiveLayer();
    if (!layer || layer.locked || !layer.visible) return;

    // Alt+Click sets clone source
    if (e.altKey) {
      this.editor.toolSettings.cloneSource = { x: canvasX, y: canvasY };
      this.editor.showToast('Clone source set at ' + Math.round(canvasX) + ', ' + Math.round(canvasY));
      this.editor.renderOverlayOnly();
      return;
    }

    this.isPainting = true;
    const lx = canvasX - layer.x;
    const ly = canvasY - layer.y;

    if (this.editor.toolSettings.healingMode === 'clone') {
      this.applyCloneStamp(layer, lx, ly);
    } else {
      this.applySpotHealing(layer, lx, ly);
    }
    this.editor.render();
  }

  public onMouseMove(e: MouseEvent, canvasX: number, canvasY: number): void {
    this.currentMouseX = canvasX;
    this.currentMouseY = canvasY;

    if (!this.isPainting) {
      this.editor.renderOverlayOnly();
      return;
    }

    const layer = this.editor.getActiveLayer();
    if (!layer || layer.locked || !layer.visible) return;

    const lx = canvasX - layer.x;
    const ly = canvasY - layer.y;

    if (this.editor.toolSettings.healingMode === 'clone') {
      this.applyCloneStamp(layer, lx, ly);
    } else {
      this.applySpotHealing(layer, lx, ly);
    }
    this.editor.render();
  }

  public onMouseUp(_e: MouseEvent): void {
    if (this.isPainting) {
      this.isPainting = false;
      this.editor.recordHistory(this.editor.toolSettings.healingMode === 'clone' ? 'Clone Stamp' : 'Spot Healing');
    }
  }

  /**
   * Blemish / Damage Repair:
   * Replaces pixels in the circle by interpolating smooth gradients and texture from the surrounding annular ring.
   */
  private applySpotHealing(layer: any, cx: number, cy: number) {
    const radius = Math.round(this.editor.toolSettings.healingRadius);
    if (radius < 2) return;

    const outerRadius = Math.round(radius * 1.5);
    const boxSize = outerRadius * 2 + 2;
    const startX = Math.max(0, Math.round(cx - outerRadius));
    const startY = Math.max(0, Math.round(cy - outerRadius));
    const endX = Math.min(layer.width, Math.round(cx + outerRadius));
    const endY = Math.min(layer.height, Math.round(cy + outerRadius));

    const w = endX - startX;
    const h = endY - startY;
    if (w <= 0 || h <= 0) return;

    const imgData = layer.ctx.getImageData(startX, startY, w, h);
    const data = imgData.data;

    // Collect healthy ring pixels (between radius and outerRadius)
    const ringPixels: { x: number; y: number; r: number; g: number; b: number; a: number }[] = [];
    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        const px = startX + x;
        const py = startY + y;
        const dist = Math.hypot(px - cx, py - cy);
        if (dist >= radius && dist <= outerRadius) {
          const idx = (y * w + x) * 4;
          ringPixels.push({
            x: px,
            y: py,
            r: data[idx],
            g: data[idx + 1],
            b: data[idx + 2],
            a: data[idx + 3]
          });
        }
      }
    }

    if (ringPixels.length === 0) return;

    // Inverse distance weighting (Shepard's method) to heal the blemish region
    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        const px = startX + x;
        const py = startY + y;
        const distToCenter = Math.hypot(px - cx, py - cy);

        if (distToCenter < radius) {
          let sumWeight = 0;
          let r = 0, g = 0, b = 0, a = 0;

          // Sample around the ring (limit sampling for fast 60fps)
          const step = Math.max(1, Math.floor(ringPixels.length / 32));
          for (let i = 0; i < ringPixels.length; i += step) {
            const p = ringPixels[i];
            const d = Math.hypot(px - p.x, py - p.y);
            const weight = 1 / Math.pow(Math.max(1, d), 1.8);
            sumWeight += weight;
            r += p.r * weight;
            g += p.g * weight;
            b += p.b * weight;
            a += p.a * weight;
          }

          const idx = (y * w + x) * 4;
          // Smooth blend based on distance from center
          const blendFactor = Math.pow(1 - distToCenter / radius, 1.2);
          const healedR = r / sumWeight;
          const healedG = g / sumWeight;
          const healedB = b / sumWeight;
          const healedA = a / sumWeight;

          data[idx] = Math.round(data[idx] * (1 - blendFactor) + healedR * blendFactor);
          data[idx + 1] = Math.round(data[idx + 1] * (1 - blendFactor) + healedG * blendFactor);
          data[idx + 2] = Math.round(data[idx + 2] * (1 - blendFactor) + healedB * blendFactor);
          data[idx + 3] = Math.round(data[idx + 3] * (1 - blendFactor) + healedA * blendFactor);
        }
      }
    }

    layer.ctx.putImageData(imgData, startX, startY);
  }

  /**
   * Clone Stamp: Copies from source position with radial feathering
   */
  private applyCloneStamp(layer: any, targetX: number, targetY: number) {
    const src = this.editor.toolSettings.cloneSource;
    if (!src) {
      this.editor.showToast('Please Alt+Click first to select clone source point');
      return;
    }

    const radius = Math.round(this.editor.toolSettings.healingRadius);
    const size = radius * 2;

    const sx = Math.round(src.x - radius);
    const sy = Math.round(src.y - radius);
    const tx = Math.round(targetX - radius);
    const ty = Math.round(targetY - radius);

    // Read source patch
    const srcPatch = layer.ctx.getImageData(
      Math.max(0, sx),
      Math.max(0, sy),
      Math.min(layer.width - Math.max(0, sx), size),
      Math.min(layer.height - Math.max(0, sy), size)
    );

    // Draw to target with feathering
    const tempCanvas = document.createElement('canvas');
    tempCanvas.width = srcPatch.width;
    tempCanvas.height = srcPatch.height;
    const tCtx = tempCanvas.getContext('2d');
    if (tCtx) {
      tCtx.putImageData(srcPatch, 0, 0);

      layer.ctx.save();
      layer.ctx.beginPath();
      layer.ctx.arc(targetX, targetY, radius, 0, Math.PI * 2);
      layer.ctx.clip();
      layer.ctx.drawImage(tempCanvas, tx, ty);
      layer.ctx.restore();
    }
  }

  public renderOverlay(ctx: CanvasRenderingContext2D): void {
    const zoom = this.editor.zoom;
    const panX = this.editor.panX;
    const panY = this.editor.panY;

    const sx = this.currentMouseX * zoom + panX;
    const sy = this.currentMouseY * zoom + panY;
    const radius = this.editor.toolSettings.healingRadius * zoom;

    ctx.save();
    // Cursor circle
    ctx.beginPath();
    ctx.arc(sx, sy, radius, 0, Math.PI * 2);
    ctx.strokeStyle = '#10b981';
    ctx.lineWidth = 1.5;
    ctx.stroke();

    // If clone source is set, draw crosshair at source
    if (this.editor.toolSettings.healingMode === 'clone' && this.editor.toolSettings.cloneSource) {
      const cs = this.editor.toolSettings.cloneSource;
      const csX = cs.x * zoom + panX;
      const csY = cs.y * zoom + panY;

      ctx.strokeStyle = '#f59e0b';
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.arc(csX, csY, 8, 0, Math.PI * 2);
      ctx.moveTo(csX - 12, csY);
      ctx.lineTo(csX + 12, csY);
      ctx.moveTo(csX, csY - 12);
      ctx.lineTo(csX, csY + 12);
      ctx.stroke();
    }

    ctx.restore();
  }
}
